import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import toast from "react-hot-toast";


export interface DropzoneFile {
  id: string | number;
  file: File;
  valid: boolean;
  errors?: string[];
  uploadMessage?: string;
  uploadStatus?: 'uploading' | 'success' | 'error';
  xhr?: XMLHttpRequest;
  videoUrl?: string;
  duration?: number;
}

export const loadFFmpeg = async (ffmpegRef, setFfmpegLoaded) => {
  const ffmpeg = ffmpegRef.current;
  
  if (ffmpeg.loaded) {
    console.log('✅ FFmpeg ya cargado');
    setFfmpegLoaded(true);
    return;
  }

  try {
    console.log('🔄 Iniciando carga de FFmpeg...');
    
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    
    const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
    const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
    
    await ffmpeg.load({
      coreURL,
      wasmURL,
    });
    
    console.log('✅ FFmpeg cargado exitosamente');
    setFfmpegLoaded(true);
  } catch (error) {
    console.error('❌ Error cargando FFmpeg:', error);
    toast.error('Error al cargar el conversor de video. Recarga la página.');
  }
};

export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = () => {
      reject(new Error('Error al cargar el video'));
    };

    video.src = URL.createObjectURL(file);
  });
};

export const convertMovToMp4 = async (file: File, ffmpegLoaded: boolean, ffmpegRef: React.RefObject<FFmpeg>): Promise<File> => {
  if (!ffmpegLoaded) {
    throw new Error('FFmpeg no está cargado');
  }

  const ffmpeg = ffmpegRef.current;
  
  console.log('🎬 Iniciando conversión de', file.name, `(${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
  
  try {
    //* Limpiar archivos anteriores
    try {
      await ffmpeg.deleteFile('input.mov');
      await ffmpeg.deleteFile('output.mp4');
    } catch (e) {
      // Ignorar si no existen
    }
    
    console.log('📝 Escribiendo archivo...');
    await ffmpeg.writeFile('input.mov', await fetchFile(file));
    console.log('✅ Archivo escrito');
    
    //* Log listener (opcional, para debugging)
    // ffmpeg.on('log', ({ message }) => {
    //   console.log('FFmpeg:', message);
    // });
    
    console.log('⚙️ Ejecutando conversión...');
    await ffmpeg.exec([
      '-i', 'input.mov',
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-crf', '30',
      '-maxrate', '2.5M',
      '-bufsize', '5M',
      // '-vf', 'scale=1280:720',
      '-r', '30',
      '-c:a', 'aac',
      '-b:a', '96k',
      '-ac', '2',
      '-movflags', '+faststart',
      '-pix_fmt', 'yuv420p',
      'output.mp4'
    ]);
    console.log('✅ Conversión completada');
    
    console.log('📖 Leyendo resultado...');
    const data = await ffmpeg.readFile('output.mp4');
    console.log('✅ Resultado leído:', (data.length / (1024 * 1024)).toFixed(2), 'MB');
    
    const mp4Blob = new Blob([data as BlobPart], { type: 'video/mp4' });
    const convertedFile = new File(
      [mp4Blob], 
      file.name.replace(/\.mov$/i, '.mp4'), 
      { type: 'video/mp4' }
    );
    
    //* Limpiar
    await ffmpeg.deleteFile('input.mov');
    await ffmpeg.deleteFile('output.mp4');
    
    return convertedFile;
    
  } catch (error) {
    console.error('❌ Error en conversión:', error);
    throw error;
  }
};


